import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Edit } from "@mui/icons-material";
import {
  Good,
  TrailerCompartmentAssignment,
  TrailerCompartmentUnit,
} from "../../../graphql/generated";
import enumLabel from "../../../utils/labels/enumLabel";
import { formatDateTime } from "../../../utils/labels/formatDateTime";
import unloadedTractor from "../../../assets/icons/unloaded-tractor.svg";

// ──────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────

export type CompartmentCellData = {
  identifier: string;
  capacity: number;
  unit: TrailerCompartmentUnit;
  hasDoubleBulkheadAfter?: boolean | null;
  trailerId: string;
  maxVolume?: number | null;
};

/** Minimal shape for a CompartmentVolume record (generated type does not exist yet). */
export type CompartmentVolume = {
  _id?: string;
  compartmentId: string;
  trailerId?: string;
  commodityId?: string;
  quantity: number;
  lastUpdated?: string | Date | null;
  lastShipmentId?: string | null;
  lastShipmentNumber?: string | null;
  updatedBy?: string | null;
};

/** A shipped good (from trip / shipment shippedGoods). */
export type ShippedGood = Pick<Good, "_id" | "label"> & {
  goodProfileId?: string | null;
  color?: string | null;
};

export type CompartmentGraphicProps = {
  /** Ordered list of compartments (index 0 = front, displayed on the right). */
  compartments: CompartmentCellData[];

  // Assignment layer
  compartmentAssignments?: TrailerCompartmentAssignment[];
  availableGoods?: ShippedGood[];
  selectedCompartmentId?: string | null;
  onCompartmentClick?: (compartmentId: string, trailerId: string) => void;

  // Volume tracking layer
  compartmentVolumes?: CompartmentVolume[];
  onEditVolume?: (compartmentId: string, trailerId: string) => void;
  /** Optional lookup map for resolving commodity labels from commodityId */
  goodProfilesMap?: Record<string, string>;
  /** When true, suppresses the per-compartment metadata row (lastUpdated, lastShipmentId, updatedBy) */
  hideVolumeMetadata?: boolean;
};

// ──────────────────────────────────────────────────────────────
// Utility
// ──────────────────────────────────────────────────────────────

/** Returns the maximum fill for a compartment: maxVolume if defined, otherwise capacity. */
export function maxFill(
  compartment: Pick<CompartmentCellData, "capacity" | "maxVolume">
): number {
  return compartment.maxVolume ?? compartment.capacity;
}

// ──────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────

const CompartmentGraphic: React.FC<CompartmentGraphicProps> = ({
  compartments,
  compartmentAssignments,
  availableGoods,
  selectedCompartmentId,
  onCompartmentClick,
  compartmentVolumes,
  onEditVolume,
  goodProfilesMap,
  hideVolumeMetadata,
}) => {
  const hasAssignmentLayer = compartmentAssignments !== undefined;
  const hasVolumeLayer = compartmentVolumes !== undefined;

  if (compartments.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No compartments defined
        </Typography>
      </Box>
    );
  }

  const totalCapacity = compartments.reduce((sum, c) => sum + c.capacity, 0);

  // Reverse for display so index 0 (front) appears on the right, adjacent to the
  // tractor icon. Compartments read back-to-front from left to right.
  const displayCompartments = [...compartments].reverse();

  return (
    // Outer wrapper: position:relative so the absolutely-placed truck icon
    // is anchored here but doesn't affect the flow of cells or metadata.
    <Box sx={{ position: "relative", paddingRight: "80px", paddingBottom: "40px" }}>

      {/* ── Graphic row: compartment cells in normal flow ── */}
      <Stack direction="row" sx={{ height: 80, alignItems: "stretch", gap: "4px" }}>
        {displayCompartments.map((compartment, index) => {
          const widthPct =
            totalCapacity > 0
              ? (compartment.capacity / totalCapacity) * 100
              : 100 / displayCompartments.length;

          const isFirst = index === 0;
          const isLast = index === displayCompartments.length - 1;

          const isDoubleBulkhead = compartment.hasDoubleBulkheadAfter === true;

          // Solid 4px border → Double Bulkhead (heavy only)
          // Dashed 3px border → standard (any product)
          const borderStyle = isDoubleBulkhead ? "4px solid #000" : "3px dashed #000";

          // Assignment layer data
          const assignment = hasAssignmentLayer
            ? compartmentAssignments?.find(
                (a) =>
                  a.compartmentId === compartment.identifier &&
                  (!a.trailerId || a.trailerId === compartment.trailerId)
              )
            : null;

          const assignedGood =
            assignment && availableGoods
              ? availableGoods.find((g) => g._id === assignment.commodityId)
              : null;

          // Volume layer data (declared before fillPct so we can combine both)
          const volume = hasVolumeLayer
            ? compartmentVolumes?.find(
                (v) =>
                  v.compartmentId === compartment.identifier &&
                  (!v.trailerId || v.trailerId === compartment.trailerId)
              )
            : null;

          // Fill = (existing volume + assigned quantity) / capacity, clamped to 100%
          const existingVolume = volume?.quantity ?? 0;
          const assignedQuantity = assignment?.quantity ?? 0;
          const totalFill = existingVolume + assignedQuantity;
          const fillPct =
            compartment.capacity > 0 && totalFill > 0
              ? Math.min(100, (totalFill / compartment.capacity) * 100)
              : 0;

          const isSelected =
            selectedCompartmentId === compartment.identifier &&
            onCompartmentClick !== undefined;

          const clickable = onCompartmentClick !== undefined;

          return (
            <Box
              key={`${compartment.trailerId}-${compartment.identifier}`}
              data-testid={`compartment-cell-${compartment.identifier}`}
              data-double-bulkhead={compartment.hasDoubleBulkheadAfter === true ? "true" : "false"}
              onClick={
                clickable
                  ? () =>
                      onCompartmentClick(
                        compartment.identifier,
                        compartment.trailerId
                      )
                  : undefined
              }
              sx={(theme) => ({
                position: "relative",
                width: `${widthPct}%`,
                height: "100%",
                backgroundColor: theme.palette.primary.light,
                // Double-bulkhead: thick solid border — clearly "heavy only"
                // Standard: thick dashed border — any product
                border: borderStyle,
                borderTopLeftRadius: isFirst ? 8 : 0,
                borderBottomLeftRadius: isFirst ? 8 : 0,
                borderTopRightRadius: isLast ? 8 : 0,
                borderBottomRightRadius: isLast ? 8 : 0,
                overflow: "hidden",
                outline: isSelected
                  ? `3px solid ${theme.palette.primary.main}`
                  : "none",
                outlineOffset: "1px",
                cursor: clickable ? "pointer" : "default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                "&:hover": clickable
                  ? { backgroundColor: theme.palette.primary.main }
                  : undefined,
              })}
            >
              {/* Fill bar — shown when there's an assignment OR a volume record */}
              {((hasAssignmentLayer && assignment) || (hasVolumeLayer && volume)) && fillPct > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: `${fillPct}%`,
                    backgroundColor:
                      (assignedGood as any)?.color ||
                      (assignedGood as any)?.goodProfileColor ||
                      "#90caf9",
                    opacity: 0.6,
                    zIndex: 0,
                  }}
                />
              )}

              {/* Edit icon (volume layer) */}
              {hasVolumeLayer && onEditVolume && (
                <Tooltip title="Edit volume">
                  <IconButton
                    size="small"
                    data-testid={`edit-volume-${compartment.identifier}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditVolume(compartment.identifier, compartment.trailerId);
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      zIndex: 2,
                      p: 0.5,
                      backgroundColor: "rgba(255,255,255,0.75)",
                      color: "primary.dark",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Cell text content */}
              <Box sx={{ position: "relative", zIndex: 1, px: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, display: "block", lineHeight: 1.2 }}
                  color="primary.contrastText"
                >
                  {compartment.identifier}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", lineHeight: 1.2 }}
                  color="primary.contrastText"
                >
                  {compartment.capacity} {enumLabel(compartment.unit)}
                </Typography>

                {/* Assignment layer: commodity label + quantity */}
                {hasAssignmentLayer && assignment && assignedGood && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", lineHeight: 1.2 }}
                    color="primary.contrastText"
                  >
                    {assignedGood.label} ({assignment.quantity})
                  </Typography>
                )}

                {/* Volume layer: current commodity label + volume */}
                {hasVolumeLayer && volume && volume.quantity > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", lineHeight: 1.2, fontStyle: "italic" }}
                    color="primary.contrastText"
                  >
                    {volume.commodityId && goodProfilesMap?.[volume.commodityId]
                      ? `${goodProfilesMap[volume.commodityId]}: `
                      : ""}
                    {volume.quantity}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
        </Stack>

      {/* Truck icon — absolutely positioned relative to the outer wrapper,
          bottom-aligned so it sits at the same baseline as the cells.
          pointerEvents:none so it never intercepts clicks on cells or edit buttons. */}
      <img
        src={unloadedTractor}
        alt="Unloaded tractor"
        style={{
          position: "absolute",
          right: 0,
          top: 10,
          height: 110,
          pointerEvents: "none",
        }}
      />

      {/* ── Metadata row (volume layer only, only when there are actual records) ── */}
      {hasVolumeLayer && !hideVolumeMetadata && compartmentVolumes!.length > 0 && (
        <Stack direction="row" sx={{ width: "100%", mt: 0.5 }}>
          {displayCompartments.map((compartment) => {
            const widthPct =
              totalCapacity > 0
                ? (compartment.capacity / totalCapacity) * 100
                : 100 / displayCompartments.length;

            const volume = compartmentVolumes?.find(
              (v) =>
                v.compartmentId === compartment.identifier &&
                (!v.trailerId || v.trailerId === compartment.trailerId)
            );

            const lastUpdated = volume?.lastUpdated
              ? formatDateTime(volume.lastUpdated)
              : "—";
            const lastShipment = volume?.lastShipmentNumber || "—";
            const updatedBy = volume?.updatedBy || "—";

            return (
              <Box
                key={`meta-${compartment.trailerId}-${compartment.identifier}`}
                sx={{ width: `${widthPct}%`, px: 0.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{ display: "block", lineHeight: 1.3, fontSize: "0.65rem" }}
                  color="text.secondary"
                >
                  {lastUpdated}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", lineHeight: 1.3, fontSize: "0.65rem" }}
                  color="text.secondary"
                >
                  {lastShipment}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", lineHeight: 1.3, fontSize: "0.65rem" }}
                  color="text.secondary"
                >
                  {updatedBy}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default CompartmentGraphic;
